import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, Link, useLocation } from 'react-router-dom';
import QuestionMarkIcon from './components/QuestionMarkIcon';
import EducationModal from './components/EducationModal';
import { AuthProvider, useAuth } from './context/authContext';
import { WalkthroughProvider } from './context/walkthroughContext';
import { MenuSchedulingProvider, useMenuScheduling } from './context/menuSchedulingContext';
import { TabsProvider } from './context/TabsContext';
import { CompareProvider } from './context/CompareContext';
import { WalkthroughChefProvider } from './context/WalkthroughChefContext';
import WalkthroughManager from './components/WalkthroughManager';
import MenuSchedulingPopup from './components/menuScheduling/MenuSchedulingPopup';
import ProtectedRoute from './components/ProtectedRoute';
import UserButtons from './components/UserButtons';
import PremiumFeatures from './components/PremiumFeatures';
import Footer from './components/Footer';
import { getSubscriptionStatus } from './services/subscriptionService';
import FloatingChef from './components/FloatingChef';
import FloatingPantryTab from './components/FloatingPantryTab';
import HeaderToggleTab from './components/HeaderToggleTab';
import useHeaderToggle from './hooks/useHeaderToggle';

// Import pages
import Explore from './pages/Explore';
import RecipeDetails from './pages/RecipeDetails';
import CreateRecipe from './pages/CreateRecipe';
import EditRecipe from './pages/EditRecipe';
import Login from './pages/Login';
import Register from './pages/Register';
import ProfilePage from './pages/ProfilePage';
import NotificationsPage from './pages/NotificationsPage';
import SousChefPage from './pages/SousChefPage';
import NotFound from './pages/NotFound';
import ForkRecipe from './pages/ForkRecipe';
import Cache from './pages/MyRecipes';
import CollectionDetail from './pages/CollectionDetail';
import RecipeQuiz from './pages/RecipeQuiz';
import Nutrition from './pages/Nutrition';
import StyleGuide from './pages/StyleGuide';
import About from './pages/About';
import Bases from './pages/Bases';
import CreateEditKernel from './pages/CreateEditKernel';
import TheTable from './pages/TheTable';

import DraftViewer from './pages/DraftViewer';
import CookNow from './pages/CookNow';
import LiveSession from './pages/LiveSession';
import Activity from './components/Activity';
import AdminDashboard from './pages/AdminDashboard';
import ChefVoiceDemo from './pages/ChefVoiceDemo';
import IngredientSearch from './pages/IngredientSearch';
import MenuPlanningPage from './pages/MenuPlanningPage';
import MenuPlanningOccasionsPage from './pages/MenuPlanningOccasionsPage';
import MenuSchedulingPage from './pages/MenuSchedulingPage';
import PlanningHubPage from './pages/PlanningHubPage';
import Schedule from './pages/Schedule';
import WTTest from './pages/WTTest';
import StockYourKitchenPage from './pages/StockYourKitchenPage';
import GenerateGroceryListPage from './pages/GenerateGroceryListPage';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DMCAPolicy from './pages/DMCAPolicy';
import FAQ from './pages/FAQ';

// Import new UI components
import { PageHeading } from './components/ui';

// Redirect helpers
const RedirectToRecipes = () => {
    const { id } = useParams();
    return <Navigate to={id ? `/recipes/${id}` : '/recipes'} replace />;
};
const RedirectToBases = () => <Navigate to="/bases" replace />;


// Navigation component that can access auth context
const Navigation = ({ isHeaderVisible }) => {
    const { userProfile } = useAuth();
    const location = useLocation();
    
    // Check if we're on a recipe details page (for footer hiding)
    const isRecipeDetailsPage = location.pathname.startsWith('/recipes/') && location.pathname.split('/').length === 3;
    const isAdmin = userProfile?.is_admin;
    
    // Get menu scheduling context - must be called unconditionally
    const { openScheduleModal, isSchedulingMode } = useMenuScheduling();
    
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [hoveredFeature, setHoveredFeature] = useState(null);
    const [subscription, setSubscription] = useState(null);
    
    // Education modal states for navigation items
    const [showRecipesModal, setShowRecipesModal] = useState(false);
    const [showExploreModal, setShowExploreModal] = useState(false);
    const [showTableModal, setShowTableModal] = useState(false);
    const [showAdminModal, setShowAdminModal] = useState(false);
    
    // Fetch subscription status
    useEffect(() => {
        const fetchSubscription = async () => {
            // Only fetch if user is authenticated
            if (!userProfile) {
                return;
            }
            
            try {
                const status = await getSubscriptionStatus();
                // Service now returns null instead of throwing when no user
                if (status) {
                setSubscription(status);
                }
            } catch (error) {
                // Only log if user is authenticated (actual errors)
                if (userProfile) {
                console.error('Failed to fetch subscription status:', error);
                }
            }
        };

            fetchSubscription();
    }, [userProfile]);
    
    // Helper function to check if a link is active
    const isActive = (path) => {
        if (path === '/') {
            return location.pathname === '/';
        }
        return location.pathname.startsWith(path);
    };
    
    // Check if user has premium access
    const isPremiumUser = subscription && ['unlimited', 'beta', 'admin', 'family'].includes(subscription.planType);
    
    return (
        <>
        <nav className={`bg-neutral-50 sticky top-5 z-20 w-full hairline-t hairline-b pb-0 overflow-visible ${
            isHeaderVisible ? 'mt-4' : 'mt-0'
        }`}>
            <div className="w-full px-1.5 sm:px-2.5 md:px-3.5">
                <div className="flex justify-between items-center py-1 sm:py-1.5 md:py-2">
                    {/* Left side: Main nav + Admin */}
                    <div className="flex items-center space-x-8">
                        <div className="relative group">
                            <Link 
                                to="/recipes" 
                                data-walkthrough="recipes-nav-button"
                                className={`nav-link ${isActive('/recipes') ? 'active' : ''}`}
                            >
                                RECIPES
                            </Link>
                            <QuestionMarkIcon 
                                onOpenModal={() => setShowRecipesModal(true)} 
                                onCloseModal={() => setShowRecipesModal(false)}
                                isModalOpen={showRecipesModal}
                            />
                        </div>
                        <div className="relative group">
                            <Link 
                                to="/explore" 
                                className={`nav-link ${isActive('/explore') ? 'active' : ''}`}
                            >
                                EXPLORE
                            </Link>
                            <QuestionMarkIcon 
                                onOpenModal={() => setShowExploreModal(true)} 
                                onCloseModal={() => setShowExploreModal(false)}
                                isModalOpen={showExploreModal}
                            />
                        </div>
                        <div className="relative group">
                            <Link 
                                to="/the-table" 
                                className={`nav-link ${isActive('/the-table') ? 'active' : ''}`}
                            >
                                THE TABLE
                            </Link>
                            <QuestionMarkIcon 
                                onOpenModal={() => setShowTableModal(true)} 
                                onCloseModal={() => setShowTableModal(false)}
                                isModalOpen={showTableModal}
                            />
                        </div>
                        {/* Planning Hub - Show when there's space, for premium users */}
                        {isPremiumUser && (
                            <div className="hidden lg:block relative group">
                                <Link 
                                    to="/planning-hub" 
                                    className={`nav-link ${isActive('/planning-hub') ? 'active' : ''}`}
                                >
                                    PLANNING HUB
                                </Link>
                            </div>
                        )}
                    </div>
                    
                    {/* Right side: Menu */}
                    <div className="flex items-center space-x-4">
                        {/* Menu - Always visible */}
                        <div className="relative flex items-center">
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className={`font-mono text-base uppercase tracking-wider transition-all duration-120 p-0 m-0 h-auto leading-none inline-flex items-center min-h-0 min-w-0 ${
                                    isMenuOpen 
                                        ? 'text-purple-400 hover:text-neutral-500' 
                                        : 'text-neutral-900 opacity-60 hover:text-neutral-500'
                                }`}
                                style={{ 
                                    padding: 0, 
                                    margin: 0, 
                                    border: 'none', 
                                    background: 'transparent',
                                    minHeight: 0,
                                    minWidth: 0,
                                    lineHeight: 'inherit'
                                }}
                                aria-label="Toggle menu"
                            >
                                <svg 
                                    width="14" 
                                    height="14" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    className="inline-block"
                                >
                                    <path d="M3 12h18M3 6h18M3 18h18"/>
                                </svg>
                            </button>
                            
                            {/* Dropdown Menu - With opaque background */}
                            {isMenuOpen && (
                                <div className="absolute top-full right-0 mt-2 w-56 bg-neutral-900 text-neutral-0 border border-neutral-200 shadow-lg z-50">
                                    <div className="py-2">
                                        {/* Premium Features Section */}
                                        <div className="px-4 py-2 text-right">
                                            <div className="text-xs font-mono uppercase tracking-wider text-neutral-0 mb-2">PREMIUM FEATURES</div>
                                            
                                            {/* Planning Hub - Show in menu when screen is too small for nav bar (lg:hidden means hidden on large screens where nav bar shows it) */}
                                            {isPremiumUser && (
                                                <div className="mb-1 relative group lg:hidden">
                                                    <Link 
                                                        to="/planning-hub" 
                                                        className="text-neutral-0 font-mono text-base uppercase tracking-wider hover:opacity-80 transition-opacity flex items-center gap-2"
                                                        onClick={() => setIsMenuOpen(false)}
                                                    >
                                                        <span className="px-1.5 py-0.5 bg-[#1b263b] text-neutral-0 font-mono text-xs uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-120">BETA</span>
                                                        Planning Hub
                                                    </Link>
                                                </div>
                                            )}
                                            
                                            {/* Grocery List - Premium users can access */}
                                            <div className="mb-1 relative group">
                                                {isPremiumUser ? (
                                                    <Link 
                                                        to="/grocery/generate" 
                                                        className="text-neutral-0 font-mono text-base uppercase tracking-wider hover:opacity-80 transition-opacity flex items-center gap-2"
                                                        onClick={() => setIsMenuOpen(false)}
                                                    >
                                                        <span className="px-1.5 py-0.5 bg-[#1b263b] text-neutral-0 font-mono text-xs uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-120">BETA</span>
                                                        Grocery List
                                                    </Link>
                                                ) : (
                                                    <>
                                                        <div 
                                                            className="text-neutral-0 opacity-40 cursor-not-allowed font-mono text-base uppercase tracking-wider"
                                                            onMouseEnter={() => setHoveredFeature('grocery')}
                                                            onMouseLeave={(e) => {
                                                                if (!e.relatedTarget?.closest('.coming-soon-popup')) {
                                                                    setHoveredFeature(null);
                                                                }
                                                            }}
                                                            onClick={() => {
                                                                setIsMenuOpen(false);
                                                            }}
                                                        >
                                                            Grocery List
                                                        </div>
                                                        {/* Coming Soon Popup */}
                                                        <div 
                                                            className={`coming-soon-popup absolute right-full top-0 mr-2 w-64 bg-neutral-900 text-neutral-0 p-4 border border-neutral-200 shadow-lg z-50 transition-opacity duration-200 ${hoveredFeature === 'grocery' ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                                                            onMouseEnter={() => setHoveredFeature('grocery')}
                                                            onMouseLeave={() => setHoveredFeature(null)}
                                                        >
                                                            <div className="font-mono text-base uppercase tracking-wider mb-2">
                                                                PREMIUM FEATURE
                                                            </div>
                                                            <div className="text-base mb-2">
                                                                This feature requires a premium subscription
                                                            </div>
                                                            <div className="text-sm opacity-80">
                                                                Upgrade to access grocery list features
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            
                                            {/* Menu Planning - Premium feature */}
                                            <div className="mb-1 relative group">
                                                {isPremiumUser ? (
                                                    <Link 
                                                        to="/menu-planning" 
                                                        className="text-neutral-0 font-mono text-base uppercase tracking-wider hover:opacity-80 transition-opacity flex items-center gap-2"
                                                        onClick={() => setIsMenuOpen(false)}
                                                    >
                                                        <span className="px-1.5 py-0.5 bg-[#1b263b] text-neutral-0 font-mono text-xs uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-120">BETA</span>
                                                        Menu Planning
                                                    </Link>
                                                ) : (
                                                    <>
                                                        <div 
                                                            className="text-neutral-0 opacity-40 cursor-not-allowed font-mono text-base uppercase tracking-wider"
                                                            onMouseEnter={() => setHoveredFeature('menu-planning')}
                                                            onMouseLeave={(e) => {
                                                                if (!e.relatedTarget?.closest('.coming-soon-popup')) {
                                                                    setHoveredFeature(null);
                                                                }
                                                            }}
                                                            onClick={() => {
                                                                setIsMenuOpen(false);
                                                            }}
                                                        >
                                                            Menu Planning
                                                        </div>
                                                        {/* Premium Feature Popup */}
                                                        <div 
                                                            className={`coming-soon-popup absolute right-full top-0 mr-2 w-64 bg-neutral-900 text-neutral-0 p-4 border border-neutral-200 shadow-lg z-50 transition-opacity duration-200 ${hoveredFeature === 'menu-planning' ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                                                            onMouseEnter={() => setHoveredFeature('menu-planning')}
                                                            onMouseLeave={() => setHoveredFeature(null)}
                                                        >
                                                            <div className="font-mono text-base uppercase tracking-wider mb-2">
                                                                PREMIUM FEATURE
                                                            </div>
                                                            <div className="text-base mb-2">
                                                                This feature requires a premium subscription
                                                            </div>
                                                            <div className="text-sm opacity-80">
                                                                Upgrade to access menu planning features
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            
                                            {/* Menu Scheduling - Premium feature */}
                                            <div className="mb-1 relative group">
                                                {isPremiumUser ? (
                                                    <Link 
                                                        to="/menu-scheduling" 
                                                        className="text-neutral-0 font-mono text-base uppercase tracking-wider hover:opacity-80 transition-opacity flex items-center gap-2"
                                                        onClick={() => setIsMenuOpen(false)}
                                                    >
                                                        <span className="px-1.5 py-0.5 bg-[#1b263b] text-neutral-0 font-mono text-xs uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-120">BETA</span>
                                                        Menu Scheduling
                                                    </Link>
                                                ) : (
                                                    <>
                                                        <div 
                                                            className="text-neutral-0 opacity-40 cursor-not-allowed font-mono text-base uppercase tracking-wider"
                                                            onMouseEnter={() => setHoveredFeature('menu-scheduling')}
                                                            onMouseLeave={(e) => {
                                                                if (!e.relatedTarget?.closest('.coming-soon-popup')) {
                                                                    setHoveredFeature(null);
                                                                }
                                                            }}
                                                            onClick={() => {
                                                                setIsMenuOpen(false);
                                                            }}
                                                        >
                                                            Menu Scheduling
                                                        </div>
                                                        {/* Premium Feature Popup */}
                                                        <div 
                                                            className={`coming-soon-popup absolute right-full top-0 mr-2 w-64 bg-neutral-900 text-neutral-0 p-4 border border-neutral-200 shadow-lg z-50 transition-opacity duration-200 ${hoveredFeature === 'menu-scheduling' ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                                                            onMouseEnter={() => setHoveredFeature('menu-scheduling')}
                                                            onMouseLeave={() => setHoveredFeature(null)}
                                                        >
                                                            <div className="font-mono text-base uppercase tracking-wider mb-2">
                                                                PREMIUM FEATURE
                                                            </div>
                                                            <div className="text-base mb-2">
                                                                This feature requires a premium subscription
                                                            </div>
                                                            <div className="text-sm opacity-80">
                                                                Upgrade to access menu scheduling features
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            
                                            {/* Ingredient Search - Always in menu */}
                                            <div className="mb-1 relative group">
                                                {isPremiumUser ? (
                                                    <Link 
                                                        to="/ingredient-search" 
                                                        className="text-neutral-0 font-mono text-base uppercase tracking-wider hover:opacity-80 transition-opacity flex items-center gap-2"
                                                        onClick={() => setIsMenuOpen(false)}
                                                    >
                                                        <span className="px-1.5 py-0.5 bg-[#1b263b] text-neutral-0 font-mono text-xs uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-120">BETA</span>
                                                        Ingredient Search
                                                    </Link>
                                                ) : (
                                                    <>
                                                        <div 
                                                            className="text-neutral-0 opacity-40 cursor-not-allowed font-mono text-base uppercase tracking-wider"
                                                            onMouseEnter={() => setHoveredFeature('ingredient')}
                                                            onMouseLeave={(e) => {
                                                                if (!e.relatedTarget?.closest('.coming-soon-popup')) {
                                                                    setHoveredFeature(null);
                                                                }
                                                            }}
                                                            onClick={() => {
                                                                setIsMenuOpen(false);
                                                            }}
                                                        >
                                                            Ingredient Search
                                                        </div>
                                                        {/* Coming Soon Popup */}
                                                        <div 
                                                            className={`coming-soon-popup absolute right-full top-0 mr-2 w-64 bg-neutral-900 text-neutral-0 p-4 border border-neutral-200 shadow-lg z-50 transition-opacity duration-200 ${hoveredFeature === 'ingredient' ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                                                            onMouseEnter={() => setHoveredFeature('ingredient')}
                                                            onMouseLeave={() => setHoveredFeature(null)}
                                                        >
                                                            <div className="font-mono text-base uppercase tracking-wider mb-2">
                                                                COMING SOON
                                                            </div>
                                                            <div className="text-base mb-2">
                                                                Coming soon to premium users
                                                            </div>
                                                            <div className="text-sm opacity-80">
                                                                Work in progress
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Chef Section */}
                                        <div className="border-t border-neutral-700 my-2"></div>
                                        <div className="px-4 py-2 text-right">
                                            <div className="text-xs font-mono uppercase tracking-wider text-neutral-0 mb-2">CHEF</div>
                                            
                                            <Link 
                                                to="/sous-chef" 
                                                className="text-neutral-0 font-mono text-base uppercase tracking-wider hover:opacity-80 transition-opacity block"
                                                onClick={() => setIsMenuOpen(false)}
                                            >
                                                Sous Chef Settings
                                            </Link>
                                        </div>
                                        
                                        {/* Admin Dash - Only for admins */}
                                        {isAdmin && (
                                            <>
                                                <div className="border-t border-neutral-700 my-2"></div>
                                                <div className="px-4 py-2 text-right">
                                                    <Link 
                                                        to="/admin" 
                                                        className="text-neutral-0 font-bold font-mono text-base uppercase tracking-wider hover:opacity-80 transition-opacity"
                                                        onClick={() => setIsMenuOpen(false)}
                                                    >
                                                        ADMIN DASH
                                                    </Link>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </nav>
            {/* Education Modals for Navigation Items */}
            <EducationModal
                isOpen={showRecipesModal}
                onClose={() => setShowRecipesModal(false)}
                title="My Recipes"
                description="View and manage all your personal recipes. Create new recipes, edit existing ones, organize them into collections, and access your cooking history."
                image="/images/education/recipes-demo.png"
                details={[
                    "Browse all your saved recipes in one place",
                    "Create new recipes from scratch or transfer from websites",
                    "Edit and customize recipes to your preferences",
                    "Organize recipes into collections for easy access",
                    "View your cooking history and session data",
                    "Fork recipes to create variations"
                ]}
            />
            <EducationModal
                isOpen={showExploreModal}
                onClose={() => setShowExploreModal(false)}
                title="Explore"
                description="Discover recipes from the community. Browse public recipes, search by ingredients, and find inspiration for your next cooking session."
                image="/images/education/explore-demo.png"
                details={[
                    "Browse community recipes shared by other users",
                    "Search recipes by ingredients or categories",
                    "View recipe details and cooking instructions",
                    "Fork interesting recipes to your collection",
                    "Filter and sort recipes to find what you need"
                ]}
            />
            <EducationModal
                isOpen={showTableModal}
                onClose={() => setShowTableModal(false)}
                title="The Table"
                description="Connect with friends and see their cooking activity. View what your friends are cooking, share recipes, and build your cooking community."
                image="/images/education/table-demo.png"
                details={[
                    "See your friends' cooking activity and recipes",
                    "Share recipes with your cooking community",
                    "View friend profiles and their recipe collections",
                    "Connect with other home cooks",
                    "Build your personal cooking network"
                ]}
            />
            <EducationModal
                isOpen={showAdminModal}
                onClose={() => setShowAdminModal(false)}
                title="Admin Dashboard"
                description="Administrative tools for managing the platform. View system metrics, user statistics, and platform analytics."
                image="/images/education/admin-demo.png"
                details={[
                    "View platform-wide statistics and metrics",
                    "Monitor user activity and engagement",
                    "Access system administration tools",
                    "Review recipe and session data",
                    "Manage platform settings and configurations"
                ]}
            />
        </>
    );
};

// Component to render FloatingChef conditionally (not on LiveSession)
const ConditionalFloatingChef = () => {
    const location = useLocation();
    const { userProfile } = useAuth();
    
    // Don't render on LiveSession page (it has its own with voice functionality)
    // Check for all possible LiveSession routes: /live/:draftId, /live/recipe/:recipeId, /cook/:kernelId
    const isLiveSessionPage = location.pathname.startsWith('/live/') || location.pathname.startsWith('/cook/');
    
    if (isLiveSessionPage) {
        return null;
    }
    
    return (
        <FloatingChef
            isListening={false}
            isTriggered={false}
            isEnabled={false}
            onToggle={() => {}}
            isAIThinking={false}
            isSpeaking={false}
            agentName={userProfile?.agent_name || 'Chef'}
        />
    );
};

// Component to conditionally render Footer based on location
const ConditionalFooter = () => {
    const location = useLocation();
    // Hide footer on recipe detail pages (where lineage view is shown)
    const isRecipeDetailsPage = location.pathname.startsWith('/recipes/') && location.pathname.split('/').length === 3;
    
    if (isRecipeDetailsPage) {
        return null;
    }
    
    return <Footer />;
};

// Inner component to conditionally render header tab based on location
// Must be inside BrowserRouter to use useLocation
function ConditionalHeaderTab({ isHeaderVisible, toggleHeader }) {
    const location = useLocation();
    
    // Hide header tab only on live session pages (not on recipe details pages)
    const isLiveSessionPage = location.pathname.startsWith('/live/') || location.pathname.startsWith('/cook/');
    const shouldShowHeaderTab = !isLiveSessionPage;
    
    if (!shouldShowHeaderTab) {
        return null;
    }
    
    return <HeaderToggleTab isHeaderVisible={isHeaderVisible} toggleHeader={toggleHeader} />;
}

function AppRoutes() {
    const { isHeaderVisible, toggleHeader } = useHeaderToggle();

    return (
        <AuthProvider>
            <WalkthroughProvider>
                <WalkthroughChefProvider>
                <MenuSchedulingProvider>
                <TabsProvider>
                <CompareProvider>
                <BrowserRouter>
                <AppContent isHeaderVisible={isHeaderVisible} toggleHeader={toggleHeader} />
                </BrowserRouter>
                </CompareProvider>
                </TabsProvider>
                </MenuSchedulingProvider>
                </WalkthroughChefProvider>
                </WalkthroughProvider>
            </AuthProvider>
    );
}

// Content component that has access to router context
function AppContent({ isHeaderVisible, toggleHeader }) {
    return (
        <>
            <div className="min-h-screen bg-neutral-50 px-1.5 sm:px-2.5 md:px-3.5 pt-1.5 sm:pt-2.5 md:pt-3.5">
                {/* Header Toggle Tab - hidden on lineage and live session pages */}
                <ConditionalHeaderTab isHeaderVisible={isHeaderVisible} toggleHeader={toggleHeader} />

                {/* Clean header with BF.inc aesthetic - scrolls with page */}
                <header 
                    className={`hairline-b bg-neutral-0 w-full hairline transition-all duration-300 ease-out ${
                        isHeaderVisible 
                            ? 'relative pt-2 px-4 pb-0 min-h-[140px] opacity-100 mb-0' 
                            : 'h-0 min-h-0 opacity-0 overflow-hidden p-0 m-0 pointer-events-none'
                    }`}
                >
                    <div className="absolute bottom-0 left-1.5 right-1.5 sm:left-2.5 sm:right-2.5 md:left-3.5 md:right-3.5">
                        <div className="flex items-end justify-between">
                            <Link to="/about" className="flex flex-col items-start hover:opacity-60 transition-opacity group">
                                <div className="mb-0 flex items-end">
                                    <img 
                                        src="/images/header.png" 
                                        alt="4RKED Logo" 
                                        className="inline-block hairline h-12 sm:h-16 w-auto object-contain"
                                    />
                                    <span className="text-3xl sm:text-[3.5rem] font-sans font-semibold leading-none -ml-1 text-neutral-900 group-hover:text-neutral-900">RKED</span>
                                </div>
                                <p className="font-mono text-base uppercase tracking-wider text-neutral-900 opacity-60">
                                    RECIPE SYSTEM
                                </p>
                            </Link>
                            <div>
                                <UserButtons />
                            </div>
                        </div>
                    </div>
                </header>

                {/* Main navigation - no wrapper to allow sticky behavior */}
                <Navigation isHeaderVisible={isHeaderVisible} />

                {/* Main Content */}
                <main className="w-full">
                    <Routes>
                            <Route path="/" element={<Navigate to="/explore" replace />} />
                            <Route path="/explore" element={<Explore />} />
                            <Route path="/recipes/:id" element={<RecipeDetails />} />
                            <Route path="/collections/:collectionId" element={<CollectionDetail />} />
                            <Route path="/quiz" element={<RecipeQuiz />} />
                            <Route path="/nutrition" element={<Nutrition />} />
                            <Route path="/login" element={<Login />} />
                            <Route path="/register" element={<Register />} />
                            
                            {/* Redirects for legacy paths */}
                            <Route path="/recipe/:id" element={<RedirectToRecipes />} />
                            <Route path="/cache/:id" element={<RedirectToRecipes />} />
                            <Route path="/cache" element={<Navigate to="/recipes" replace />} />
                            <Route 
                                path="/profile" 
                                element={
                                    <ProtectedRoute>
                                        <ProfilePage />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route 
                                path="/notifications" 
                                element={
                                    <ProtectedRoute>
                                        <NotificationsPage />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route 
                                path="/profile/:userId" 
                                element={
                                    <ProtectedRoute>
                                        <ProfilePage />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route 
                                path="/sous-chef" 
                                element={
                                    <ProtectedRoute>
                                        <SousChefPage />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route 
                                path="/create" 
                                element={
                                    <ProtectedRoute>
                                        <CreateRecipe />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route 
                                path="/edit/:id" 
                                element={
                                    <ProtectedRoute>
                                        <EditRecipe />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route path="/fork/:id" element={<ForkRecipe />} />
                            <Route path="/recipes" element={<Cache />} />
                            <Route 
                                path="/bases" 
                                element={
                                    <ProtectedRoute>
                                        <Bases />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route 
                                path="/the-table" 
                                element={
                                    <ProtectedRoute>
                                        <TheTable />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route 
                                path="/bases/new" 
                                element={
                                    <ProtectedRoute>
                                        <CreateEditKernel />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route 
                                path="/bases/:id/edit" 
                                element={
                                    <ProtectedRoute>
                                        <CreateEditKernel />
                                    </ProtectedRoute>
                                } 
                            />
                            {/* Legacy kernel routes */}
                            <Route path="/kernels" element={<RedirectToBases />} />
                            <Route path="/kernels/new" element={<Navigate to="/bases/new" replace />} />
                            <Route path="/kernels/:id/edit" element={<Navigate to="/bases/:id/edit" replace />} />

                            <Route 
                                path="/drafts/:id" 
                                element={
                                    <ProtectedRoute>
                                        <DraftViewer />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route 
                                path="/cook/:kernelId" 
                                element={
                                    <ProtectedRoute>
                                        <CookNow />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route 
                                path="/live/:draftId" 
                                element={
                                    <ProtectedRoute>
                                        <LiveSession />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route 
                                path="/live/recipe/:recipeId" 
                                element={
                                    <ProtectedRoute>
                                        <LiveSession />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route 
                                path="/activity" 
                                element={
                                    <ProtectedRoute>
                                        <Activity />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route path="/style-guide" element={<StyleGuide />} />
                            <Route path="/wt-test" element={<WTTest />} />
                            <Route path="/about" element={<About />} />
                            <Route path="/terms-of-service" element={<TermsOfService />} />
                            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                            <Route path="/dmca-policy" element={<DMCAPolicy />} />
                            <Route path="/faq" element={<FAQ />} />
                            
                            {/* Menu Planning Routes */}
                            <Route 
                                path="/schedule" 
                                element={
                                    <ProtectedRoute>
                                        <Schedule />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route 
                                path="/menu-planning" 
                                element={
                                    <ProtectedRoute>
                                        <MenuPlanningPage />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route 
                                path="/menu-planning/occasions" 
                                element={
                                    <ProtectedRoute>
                                        <MenuPlanningOccasionsPage />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route 
                                path="/menu-scheduling" 
                                element={
                                    <ProtectedRoute>
                                        <MenuSchedulingPage />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route 
                                path="/planning-hub" 
                                element={
                                    <ProtectedRoute>
                                        <PlanningHubPage />
                                    </ProtectedRoute>
                                } 
                            />
                            {/* Alias for grocery-list link */}
                            <Route 
                                path="/grocery-list" 
                                element={<Navigate to="/grocery/generate" replace />}
                            />
                            
                            {/* Grocery Flow Routes */}
                            <Route 
                                path="/grocery/stock" 
                                element={
                                    <ProtectedRoute>
                                        <StockYourKitchenPage />
                                    </ProtectedRoute>
                                } 
                            />
                            {/* Alias for kitchen-stock (legacy/pantry link) */}
                            <Route 
                                path="/kitchen-stock" 
                                element={
                                    <ProtectedRoute>
                                        <StockYourKitchenPage />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route 
                                path="/grocery/generate" 
                                element={
                                    <ProtectedRoute>
                                        <GenerateGroceryListPage />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route 
                                path="/chef-voice-demo" 
                                element={
                                    <ProtectedRoute>
                                        <ChefVoiceDemo />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route 
                                path="/admin" 
                                element={
                                    <ProtectedRoute>
                                        <AdminDashboard />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route 
                                path="/ingredient-search" 
                                element={
                                    <ProtectedRoute>
                                        <IngredientSearch />
                                    </ProtectedRoute>
                                } 
                            />
                            <Route path="*" element={<NotFound />} />
                    </Routes>
                </main>
                
                {/* Footer - Hidden on recipe detail pages */}
                <ConditionalFooter />
            </div>
            
            {/* Walkthrough Manager - Only show for authenticated users */}
            <WalkthroughManager />
            
            {/* Menu Scheduling Popup - Global popup for recipe selection */}
            <MenuSchedulingPopup />
            
            {/* Floating Chef - Available on all pages except LiveSession */}
            <ConditionalFloatingChef />
            
            {/* Floating Pantry Tab - Available on grocery list and pantry pages */}
            <FloatingPantryTab />
        </>
    );
}

export default AppRoutes;
